import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const TheProposal = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from('.proposal-fade', {
        y: 30,
        opacity: 0,
        duration: 0.6,
        stagger: 0.1,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 75%',
          toggleActions: 'play none none reverse',
        },
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="home-section-tight overflow-hidden">
      {/* Subtle radial glow */}
      <div className="pointer-events-none absolute top-1/2 left-1/2 h-[620px] w-[620px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-slate-300/[0.03] blur-[130px] md:h-[800px] md:w-[800px] md:blur-[150px]" />
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="home-panel proposal-fade grid gap-8 px-6 py-7 sm:px-8 sm:py-8 md:grid-cols-[1.15fr,0.85fr] md:gap-10 md:px-10 md:py-10">
          <div>
            <span className="home-kicker mb-5 block">The Pedagogy</span>
            <h2 className="mb-5 text-3xl font-display font-bold leading-tight tracking-tight text-white sm:text-4xl md:mb-6 md:text-5xl">
              From being told to
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-violet-500"> discovering.</span>
            </h2>

            <p className="mb-5 max-w-2xl text-base font-light leading-relaxed text-gray-400 md:text-lg">
              Instead of saying &lsquo;scattering grows as momentum falls,&rsquo; the site lets the learner test it. Instead of saying &lsquo;Cherenkov angle depends on speed,&rsquo; it lets them change the input and watch the ring. Instead of saying &lsquo;relativity extends the muon lifetime,&rsquo; it lets them disable dilation and see the signal collapse.
            </p>

            <p className="max-w-2xl text-base font-light leading-relaxed text-gray-400 md:text-lg">
              That shift  -  from declaration to exploration  -  is the core design. The site makes invisible physics visible through geometry, summaries, histograms, and a guide system that scaffolds every step. Different learners build understanding in different ways. This platform gives them all a way in.
            </p>
          </div>

          <div className="flex flex-col justify-between gap-8">
            <div className="grid grid-cols-3 gap-3 sm:gap-4">
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3 text-center sm:p-4">
                <div className="text-2xl font-bold text-white">7</div>
                <div className="mt-1 text-[10px] font-mono uppercase tracking-[0.22em] text-white/30">Studies</div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3 text-center sm:p-4">
                <div className="text-2xl font-bold text-white">4</div>
                <div className="mt-1 text-[10px] font-mono uppercase tracking-[0.22em] text-white/30">Surfaces</div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3 text-center sm:p-4">
                <div className="text-2xl font-bold text-white">1</div>
                <div className="mt-1 text-[10px] font-mono uppercase tracking-[0.22em] text-white/30">Goal: Intuition</div>
              </div>
            </div>

            <div className="flex flex-col items-stretch gap-4 sm:flex-row sm:items-center sm:justify-start">
              <Link
                to="/learn"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-white px-8 py-4 font-bold text-black shadow-[0_0_15px_rgba(255,255,255,0.15)] transition-all hover:scale-105 hover:bg-gray-200 sm:w-auto"
              >
                Explore the Curriculum
              </Link>
              <Link 
                to="/team" 
                className="flex items-center justify-center gap-2 text-gray-400 transition-colors hover:text-white sm:justify-start"
              >
                Meet the team &rarr;
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TheProposal;
